/**
 * Versanddialog: Beleg per E-Mail an den Partner schicken.
 *
 * Der Server schlaegt Empfaenger, Betreff und Anschreiben vor; der
 * Anwender kann alles anpassen. Verschickt wird das archivierte PDF, bei
 * Rechnungen und Gutschriften auf Wunsch zusaetzlich die XRechnung.
 * Weitere Dateien - Scans, Zeichnungen, Nebendokumente - kommen aus dem
 * Belegarchiv: Was schon am Beleg haengt, laesst sich anhaken; was in den
 * Dialog gezogen wird, landet zuerst am Beleg und ist dann angehakt. So
 * bleibt nachvollziehbar, was rausging.
 */

import { useEffect, useRef, useState } from 'react';
import type { DragEvent as ReactDragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, apiUrl } from '../api/client';
import { useToast } from './toast';
import { Checkbox, Dialog, Field, Select, TextArea, TextInput } from './ui';

export type DocumentMailInfo = {
  available: boolean;
  mode: 'off' | 'outbox' | 'graph';
  sentAt: string | null;
  draft: {
    to: string;
    subject: string;
    text: string;
    recipients: { address: string; label: string }[];
    canAttachXRechnung: boolean;
    attachXRechnung: boolean;
    replyTo: string | null;
  } | null;
};

type Attachment = {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  attachmentType: string;
};

const CUSTOM = '__andere__';
const GENERATED = new Set(['generated_pdf', 'generated_xml']);
/** Muss zur Grenze im Server passen (MAX_MAIL_ATTACHMENT_BYTES). */
const MAX_TOTAL_BYTES = 3 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

export function DocumentMailDialog({
  documentId,
  label,
  info,
  onClose,
  onSent,
}: {
  documentId: string;
  /** z. B. "Rechnung RE-2026-0004" - erscheint im Titel. */
  label: string;
  info: DocumentMailInfo;
  onClose: () => void;
  onSent: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const draft = info.draft;
  const recipients = draft?.recipients ?? [];

  const [choice, setChoice] = useState<string>(draft?.to ? draft.to : CUSTOM);
  const [customTo, setCustomTo] = useState('');
  const [subject, setSubject] = useState(draft?.subject ?? '');
  const [text, setText] = useState(draft?.text ?? '');
  const [attachXRechnung, setAttachXRechnung] = useState(draft?.attachXRechnung ?? false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const to = choice === CUSTOM ? customTo.trim() : choice;

  // Dieselbe Abfrage wie die Anhangliste der Belegseite - beide sehen
  // sofort, was der andere hochgeladen hat.
  const attachmentsQuery = useQuery({
    queryKey: ['attachments', documentId],
    queryFn: () => api.get<{ attachments: Attachment[] }>(`/api/documents/${documentId}/attachments`),
  });
  const extras = (attachmentsQuery.data?.attachments ?? []).filter(
    (attachment) => !GENERATED.has(attachment.attachmentType),
  );
  const selectedBytes = extras
    .filter((attachment) => selected.has(attachment.id))
    .reduce((sum, attachment) => sum + attachment.sizeBytes, 0);
  const tooLarge = selectedBytes > MAX_TOTAL_BYTES;

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      // Was fuer die Mail nachgereicht wird, ist ein Nebendokument - kein
      // aufbewahrungspflichtiges Original.
      form.append('attachmentType', 'other');
      form.append('note', 'Für den E-Mail-Versand hinzugefügt');
      form.append('file', file, file.name);
      const response = await fetch(apiUrl(`/api/documents/${documentId}/attachments`), {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(
          response.status,
          'UPLOAD',
          payload.message ?? 'Die Datei konnte nicht angehängt werden.',
        );
      }
      return (await response.json()) as { attachment: Attachment };
    },
    onSuccess: ({ attachment }) => {
      queryClient.invalidateQueries({ queryKey: ['attachments', documentId] });
      setSelected((current) => new Set(current).add(attachment.id));
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Der Upload ist fehlgeschlagen.'),
  });

  function addFiles(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) upload.mutate(file);
  }

  function hasFiles(event: ReactDragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  // Escape schliesst den Dialog, solange nichts laeuft.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !send.isPending && !upload.isPending) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const send = useMutation({
    mutationFn: () =>
      api.post<{ sent: boolean; to: string; attachments: string[] }>(
        `/api/documents/${documentId}/mail`,
        { to, subject, text, attachXRechnung, attachmentIds: Array.from(selected) },
      ),
    onSuccess: (result) => {
      toast.success(`${label} wurde an ${result.to} gesendet.`);
      onSent();
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Der Versand ist fehlgeschlagen.');
      }
    },
  });

  return (
    <Dialog
      title={`${label} per E-Mail senden`}
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={send.isPending}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              send.isPending || upload.isPending || tooLarge || !to || !subject.trim() || !text.trim()
            }
            onClick={() => {
              setErrors({});
              send.mutate();
            }}
          >
            {send.isPending ? 'Wird gesendet …' : 'Senden'}
          </button>
        </>
      }
    >
      <div
        onDragEnter={(event) => {
          if (!hasFiles(event)) return;
          event.preventDefault();
          event.stopPropagation();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragOver={(event) => {
          if (!hasFiles(event)) return;
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(event) => {
          if (!hasFiles(event)) return;
          event.stopPropagation();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        }}
        onDrop={(event) => {
          if (!hasFiles(event)) return;
          event.preventDefault();
          event.stopPropagation();
          dragDepth.current = 0;
          setDragging(false);
          addFiles(event.dataTransfer.files);
        }}
        style={{ position: 'relative' }}
      >
        {dragging ? (
          <div className="drop-overlay" style={{ position: 'absolute', inset: -12, zIndex: 2 }}>
            <div className="drop-overlay-box">
              <strong>Datei loslassen</strong>
              <span>wird an den Beleg gehängt und mit dieser Mail verschickt</span>
            </div>
          </div>
        ) : null}

        {info.mode === 'outbox' ? (
          <p className="faint" style={{ marginTop: 0 }}>
            Diese Installation läuft im Probemodus: Die Mail wird nicht zugestellt, sondern im
            Ausgangsordner auf dem Server abgelegt.
          </p>
        ) : null}
        {info.sentAt ? (
          <p className="faint" style={{ marginTop: 0 }}>
            Dieser Beleg wurde bereits am {new Date(info.sentAt).toLocaleString('de-DE')} versendet.
          </p>
        ) : null}

        <Field label="Empfänger" required error={errors.to}>
          {recipients.length > 0 ? (
            <Select
              value={choice}
              onChange={setChoice}
              options={[
                ...recipients.map((recipient) => ({
                  value: recipient.address,
                  label: `${recipient.address} · ${recipient.label}`,
                })),
                { value: CUSTOM, label: 'Andere Adresse …' },
              ]}
              invalid={Boolean(errors.to)}
            />
          ) : null}
          {choice === CUSTOM ? (
            <div style={{ marginTop: recipients.length > 0 ? 8 : 0 }}>
              <TextInput
                type="email"
                value={customTo}
                onChange={setCustomTo}
                placeholder="name@firma.de"
                invalid={Boolean(errors.to)}
                autoFocus={recipients.length === 0}
              />
            </div>
          ) : null}
        </Field>

        <Field label="Betreff" required error={errors.subject}>
          <TextInput
            value={subject}
            onChange={setSubject}
            maxLength={200}
            invalid={Boolean(errors.subject)}
          />
        </Field>

        <Field label="Text" required error={errors.text}>
          <TextArea value={text} onChange={setText} rows={10} />
        </Field>

        <p className="faint" style={{ marginTop: 0 }}>
          {draft?.replyTo
            ? `Absender ist das Firmenpostfach; Antworten gehen an ${draft.replyTo}.`
            : 'Absender ist das Firmenpostfach. Hinterlegen Sie Ihre E-Mail-Adresse in der Benutzerverwaltung, damit Antworten bei Ihnen landen.'}
        </p>

        <Field
          label="Anhänge"
          error={errors.attachmentIds}
          hint={
            tooLarge
              ? `Zu groß: ${formatSize(selectedBytes)} zusätzlich zum PDF. Per E-Mail gehen höchstens ${formatSize(MAX_TOTAL_BYTES)}.`
              : `Das Beleg-PDF ist immer dabei. Weitere Dateien hierher ziehen oder auswählen; höchstens ${formatSize(MAX_TOTAL_BYTES)} insgesamt.`
          }
        >
          <div style={{ display: 'grid', gap: 6 }}>
            <div className="row" style={{ gap: 12, alignItems: 'center' }}>
              <Checkbox checked disabled onChange={() => undefined} label={`${label}.pdf`} />
              {draft?.canAttachXRechnung ? (
                <Checkbox
                  checked={attachXRechnung}
                  onChange={setAttachXRechnung}
                  label="XRechnung (XML)"
                />
              ) : null}
            </div>

            {extras.map((attachment) => (
              <Checkbox
                key={attachment.id}
                checked={selected.has(attachment.id)}
                onChange={(checked) =>
                  setSelected((current) => {
                    const next = new Set(current);
                    if (checked) next.add(attachment.id);
                    else next.delete(attachment.id);
                    return next;
                  })
                }
                label={`${attachment.fileName} (${formatSize(attachment.sizeBytes)})`}
              />
            ))}

            <div className="row" style={{ gap: 8, alignItems: 'center', marginTop: 4 }}>
              <button
                type="button"
                className="btn"
                onClick={() => fileInput.current?.click()}
                disabled={upload.isPending}
              >
                {upload.isPending ? 'Wird hochgeladen …' : 'Datei hinzufügen …'}
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                  addFiles(event.target.files);
                  event.target.value = '';
                }}
              />
            </div>
          </div>
        </Field>
      </div>
    </Dialog>
  );
}
